/* eslint-disable @typescript-eslint/no-unused-vars */
import { InputText } from 'primereact/inputtext';
import { useGetProducts } from 'hooks/magentoHooks';
import { ReactElement, useState, ChangeEvent, useEffect } from 'react';
import { classNames } from 'primereact/utils';
import { ProductFieldsFragment } from 'graphql/generated/magentoApi';
import { Link } from 'react-router-dom';
import { Vendor } from 'utils/getVendors';
import useDebounce from 'utils/useDebounce';
import buttonize from 'utils/buttonize';
import './index.scss';
import useViewport from 'hooks/useViewport';
import { Button } from 'primereact/button';
import { getMarketContent } from 'utils/marketContent';

type Collection = {
  collection_url: string;
  collection_image: string;
  collection_name: string;
};

export default function SearchMenu(): ReactElement {
  const market = getMarketContent();
  const dataCollections = market.collections;
  const dataVendors = market.marketVendors;
  const [valueSearch, setValueSearch] = useState('');
  const { products: allProducts } = useGetProducts();
  const [products, setProducts] =
    useState<ProductFieldsFragment[]>(allProducts);
  const [vendors, setVendors] = useState<Vendor[]>(dataVendors);
  const [collections, setCollections] = useState<Collection[]>(dataCollections);
  const { width } = useViewport();
  const isMobileWidth = width < 992;
  const [isMobile, setIsMobile] = useState(isMobileWidth);

  useEffect(() => {
    if (isMobileWidth !== isMobile) {
      setIsMobile(isMobileWidth);
    }
  }, [width, isMobile, isMobileWidth]);

  const debounce = useDebounce();

  useEffect(() => {
    const setArrays = () => {
      if (!valueSearch) {
        document.body.classList.remove(
          'fixed',
          'top-0',
          'left-0',
          'right-0',
          'bottom-0',
        );
        setProducts(allProducts);
        setVendors(dataVendors);
        setCollections(dataCollections);
      } else {
        const value = valueSearch.toLocaleLowerCase();
        document.body.classList.add(
          'fixed',
          'top-0',
          'left-0',
          'right-0',
          'bottom-0',
        );
        setProducts(
          allProducts.filter(product =>
            product.name.toLocaleLowerCase().includes(value),
          ),
        );
        setVendors(
          dataVendors.filter(vendor =>
            vendor.name.toLocaleLowerCase().includes(value),
          ),
        );
        setCollections(
          dataCollections.filter(collection =>
            collection.collection_name.toLocaleLowerCase().includes(value),
          ),
        );
      }
    };
    debounce(setArrays, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueSearch]);

  function updateSearch(e: ChangeEvent<HTMLInputElement>) {
    setValueSearch(e.currentTarget.value);
  }

  const productSearchTemplate = (item: ProductFieldsFragment) => (
    <Link
      to={`/product/${item.sku}`}
      className="flex align-items-center product-search-item"
      style={{
        textDecoration: 'none',
        fontSize: '12px !important',
        letterSpacing: '0px !important',
        textTransform: 'capitalize',
      }}
      key={item.sku}
    >
      <img
        alt={item.name || ''}
        src={item.image?.url || ''}
        height="36px"
        width="36px"
        style={{ objectFit: 'cover', borderRadius: '4px' }}
      />
      <span className="ml-2">{item.name}</span>
    </Link>
  );

  const vendorSearchTemplate = (item: Vendor) => (
    <Link
      to={`/category?vendor=${item.name}`}
      className="flex align-items-center search-vendor-item"
    >
      {item.image === '' ? (
        <span
          className="bg-primary-reverse text-center p-0 font-bold capitalize border-primary border-1"
          style={{
            objectFit: 'cover',
            borderRadius: '50%',
            height: '36px',
            width: '36px',
            lineHeight: '280%',
            aspectRatio: '1',
          }}
        >
          {item.name[0]}
        </span>
      ) : (
        <img
          alt={item.name || ''}
          src={item.image || ''}
          height="36px"
          width="36px"
          style={{ objectFit: 'cover', borderRadius: '4px' }}
        />
      )}
      <span className="ml-2">{item.name}</span>
    </Link>
  );

  const collectionSearchTemplate = (item: Collection) => (
    <Link
      to={`/collection/${item.collection_url}`}
      className="collection__item search-collection__item"
      style={{
        backgroundImage: `url(${item.collection_image})`,
        height: '150px',
        aspectRatio: '2',
      }}
    >
      <p>{item.collection_name}</p>
    </Link>
  );

  return (
    <div className="col-6 md:col-3 relative z-4 search-menu">
      <div
        style={{
          backgroundColor: '#000',
          opacity: '0.4',
        }}
        className={classNames('fixed w-screen h-screen top-0 left-0 z-3', {
          hidden: !valueSearch.length,
        })}
        {...buttonize(() => setValueSearch(''))}
      />
      <span className="p-input-icon-left z-5 w-full">
        <i className="pi pi-search" />
        <InputText
          className="w-full search-input"
          value={valueSearch}
          onChange={e => updateSearch(e)}
          aria-label="Search Products, Collections and Vendors"
          placeholder="Search"
        />
      </span>
      <div
        style={{
          backgroundColor: 'var(--surface-card)',
        }}
        className={classNames('absolute z-5 search-panel', {
          hidden: !valueSearch.length,
        })}
      >
        <div className="flex p-4  flex-column md:flex-row search-content-wrapper">
          <div className="flex-1">
            <p
              className={classNames('m-0 mb-2 search-header', {
                hidden: !products.length,
              })}
            >
              Products
            </p>
            <div
              style={{
                maxHeight: '200px',
              }}
              className={classNames('overflow-y-auto custom-scroll', {
                hidden: !products.length,
              })}
            >
              {products.map(product => productSearchTemplate(product))}
            </div>
            <p
              className={classNames('m-0 mb-2 search-header', {
                hidden: !vendors.length,
                'mt-4': !!products.length,
              })}
            >
              Vendors
            </p>
            <div
              style={{
                maxHeight: '200px',
              }}
              className={classNames('overflow-y-auto custom-scroll mb-2', {
                hidden: !vendors.length,
              })}
            >
              {vendors.map(vendor => vendorSearchTemplate(vendor))}
            </div>
          </div>
          <div className="flex-1">
            <p
              className={classNames('m-0 mb-2 search-header', {
                hidden: !collections.length,
                'ml-0 md:ml-4': !!products.length || !!vendors.length,
              })}
            >
              Collections
            </p>
            <div
              style={{
                maxHeight: '456px',
              }}
              className={classNames('overflow-y-auto custom-scroll', {
                hidden: !collections.length,
                'ml-0 md:ml-4': !!products.length || !!vendors.length,
              })}
            >
              {collections.map(collection =>
                collectionSearchTemplate(collection),
              )}
            </div>
          </div>
        </div>
        <Button
          onClick={() => setValueSearch('')}
          className="uppercase border-noround p-button-outlined p-button-secondary px-2 py-1 text-sm m-4"
          aria-label="Close summary card"
        >
          Close Search Panel
        </Button>
      </div>
    </div>
  );
}
